import { Box, Button, Tooltip } from '@mui/material'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'

import { Icon as Iconify } from '@iconify/react'
import RibbonButton from './RibbonButton'
import RibbonDropdownMenu from './RibbonDropdownMenu'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const RibbonMenu = forwardRef(
  (
    {
      label,
      icon,
      iconColor = '#FFFFFF',
      iconSize = 35,
      menuIcon = <Iconify icon='rivet-icons:menu' />,
      width = 'auto',
      height = 32,
      disabled = false,
      items = [],
      getButtonLabel,
      buttonSx = {},
      minWidth = 220,
      variant = 'default',
      clearable = false,
      searchable = false,
      menuIconOnHover = false,
      tooltip = '',
    },
    ref,
  ) => {
    const { theme, ribbonStyle } = useInnovaTheme()
    const [anchorEl, setAnchorEl] = useState(null)
    const [tooltipOpen, setTooltipOpen] = useState(false)

    const open = Boolean(anchorEl)
    const closeMenu = useCallback(() => {
      setAnchorEl(null)
      setTooltipOpen(false)
    }, [])

    useImperativeHandle(ref, () => ({
      close: () => closeMenu(),
    }))

    const borderColor = theme === 'dark' ? '#2f3a45' : '#d0d7de'
    const borderHover = theme === 'dark' ? '#3b4754' : '#c3ccd6'

    const normalizedItems = useMemo(() => {
      if (!Array.isArray(items)) return []

      return items.map((it) => {
        if (!it) return { type: 'divider' }
        if (it.type) return it
        const hasBool = typeof it.value === 'boolean' || typeof it.checked === 'boolean' || typeof it.isOn === 'boolean'
        const hasOnChange = typeof it.onChange === 'function'
        if (hasBool && hasOnChange) return { type: 'boolean', ...it }
        if (typeof it.onClick === 'function') return { type: 'action', ...it }
        return { type: 'action', ...it }
      })
    }, [items])

    const buttonText = useMemo(() => {
      if (typeof getButtonLabel === 'function') return getButtonLabel({ label, items: normalizedItems })
      return label
    }, [getButtonLabel, label, normalizedItems])

    if (variant === 'ribbonButton') {
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'stretch', height }}>
          <RibbonButton
            icon={icon}
            label={buttonText}
            iconColor={iconColor}
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            height={height}
            minWidth={48}
            iconSize={iconSize}
            reserveLabelSpace
            menuIcon={menuIcon}
            menuIconOnHover={menuIconOnHover}
            menuOpen={open}
            buttonProps={{ sx: { width, ...buttonSx } }}
          />

          <RibbonDropdownMenu
            anchorEl={anchorEl}
            open={open}
            onClose={closeMenu}
            items={normalizedItems}
            minWidth={minWidth}
            searchable={searchable || normalizedItems.length >= 15}
            clearable={clearable}
            borderColorOverride={borderColor}
          />
        </Box>
      )
    }

    const hasText = Boolean(buttonText)

    const transparentSx =
      variant === 'transparent'
        ? {
            border: 'none',
            minWidth: 0,
            padding: 0,
            backgroundColor: 'transparent',
            '&:hover': {
              border: 'none',
              backgroundColor: 'transparent',
            },
            '& .MuiButton-startIcon': {
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiButton-endIcon': {
              margin: 0,
            },
          }
        : {
            borderColor,
            backgroundColor: ribbonStyle.backgroundColor,
            '&:hover': {
              borderColor: borderHover,
              backgroundColor: ribbonStyle.backgroundColor,
            },
            '& .MuiButton-endIcon': {
              marginLeft: hasText ? 0.5 : 0,
              marginBottom: 0,
              marginRight: 0,
            },
          }

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Tooltip
          title={tooltip}
          placement='top'
          arrow
          open={!!tooltip && tooltipOpen && !open}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}>
          <Button
            variant={variant === 'transparent' ? 'text' : 'outlined'}
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={icon || null}
            endIcon={menuIcon || null}
            sx={{
              width,
              minWidth: 0,
              height,
              justifyContent: hasText ? 'space-between' : 'center',
              textTransform: 'none',
              color: ribbonStyle.color,
              ...transparentSx,
              ...buttonSx,
            }}>
            {buttonText || null}
          </Button>
        </Tooltip>

        <RibbonDropdownMenu
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          items={normalizedItems}
          minWidth={minWidth}
          searchable={searchable || normalizedItems.length >= 15}
          clearable={clearable}
          borderColorOverride={variant === 'transparent' ? undefined : borderColor}
        />
      </Box>
    )
  },
)

export default RibbonMenu
